/**
 * `.rl-empty` — the dashed inset where a reel's outcome list would be, when
 * there is nothing on the reel yet (CAE-1552).
 *
 * Not `AdminEmptyInline` from the kit. That one is a centred stack — art over
 * title over subtitle over action — and this is a row: a bubble, two lines of
 * copy and a button, all on one line inside a dashed box. They are different
 * shapes for different places, and `.rl-empty` has its own rule in the
 * stylesheet rather than reusing `.admin-empty-inline`.
 *
 * This is also the only empty state this screen has. The handoff's full-page
 * "No reels yet" is drawn for a contest with no reels at all, which a rail
 * padded to the three the contest owes never reaches — see `reel-slots.ts`.
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import { PlusIcon } from '../../../../components/ui';

export interface ReelEmptyProps {
  /** The reel's own name — "Both teams to score is empty". */
  reelTitle: string;
  /** Adding outcomes is a write, and goes when the contest's edit window does. */
  editable: boolean;
  onAddOutcomes: () => void;
}

export const ReelEmpty = ({ reelTitle, editable, onAddOutcomes }: ReelEmptyProps) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '24px',
      borderRadius: `${theme.tokens.radius.insetTile}px`,
      border: `${theme.tokens.border.width} dashed ${theme.tokens.color.lineStrong}`,
      backgroundColor: theme.tokens.color.surface,
    })}
  >
    <Box
      aria-hidden
      sx={(theme: Theme) => ({
        width: '40px',
        height: '40px',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${theme.tokens.radius.insetRow}px`,
        backgroundColor: theme.tokens.color.accentSoft,
        color: theme.tokens.color.accent,
      })}
    >
      <PlusIcon />
    </Box>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={(theme: Theme) => ({
          fontSize: '15px',
          fontWeight: 600,
          letterSpacing: '-0.005em',
          color: theme.tokens.color.ink,
        })}
      >
        {reelTitle} is empty
      </Box>
      <Box
        sx={(theme: Theme) => ({
          marginTop: '3px',
          fontSize: '13px',
          lineHeight: 1.4,
          color: theme.tokens.color.ink3,
        })}
      >
        A reel needs at least <strong>two enabled options</strong> before this contest can go live.
      </Box>
    </Box>

    <Button
      variant="contained"
      startIcon={<PlusIcon />}
      onClick={onAddOutcomes}
      disabled={!editable}
      sx={{ flexShrink: 0 }}
    >
      Add outcomes
    </Button>
  </Box>
);
